<template>
    <div class="device-map-component">
        <div class="map-container">
            <l-map
                :zoom="map.zoom"
                :center="map.center"
                :options="map.mapOptions"
                :bounds="map.bounds"
                style="height: 100%"
                @update:center="centerUpdate"
                @update:zoom="zoomUpdate"
                ref="map"
            >
                <l-tile-layer
                    :url="map.url"
                    :attribution="map.attribution"
                />
                <l-marker
                    v-for="device in deviceList"
                    v-bind:key="device.deviceId"
                    v-on:click="handleMarkerClick(device)"
                    :lat-lng="deviceLocation(device)"
                />
            </l-map>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import {LMap, LTileLayer, LMarker} from "vue2-leaflet";
import {IDeviceCoordinatesDto} from "../../Repository/Device/Dto/IDeviceCoordinatesDto";
import {LatLng, LatLngBounds} from "leaflet";
import DeviceRepository from "../../Repository/Device/DeviceRepository";
import {Watch} from "vue-property-decorator";
import {RequestGuard} from '../../utils/RequestGuard';
import {watchPageVisibility} from '../../utils/PageVisibility';
import Protector from "../Common/Protector.vue";

interface IMap {
    url: string,
    attribution: string,
    zoom: number,
    center: LatLng,
    bounds: LatLngBounds | null,
    mapOptions: any,
}

@Component({
    components: { LMap, LTileLayer, LMarker, Protector },
    props: {
        devicesOnMapList: { type: Array, required: true },
        followedDevices: { type: Array, required: false },
        focusedDevice: { type: Array, required: false },
        focusOnDevice: { type: Function, required: true, },
    }
})
class DeviceMap extends Vue {

    map: IMap = {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '',
        zoom: 11,
        center: new LatLng(55.750531, 37.621635),
        mapOptions: {},
        bounds: null,
    };

    deviceList: IDeviceCoordinatesDto[] = [];
    timer: ReturnType<typeof setInterval> | null = null;
    pollingActive = true;
    listRequests = new RequestGuard();
    focusRequests = new RequestGuard();
    stopWatchingVisibility: (() => void) | null = null;

    created() {
        this.stopWatchingVisibility = watchPageVisibility(this.onPageVisibilityChanged.bind(this));
    }

    onPageVisibilityChanged() {
        if (document.hidden) {
            this.listRequests.invalidate();
            this.focusRequests.invalidate();
            this.stopUpdate();
        } else if (this.pollingActive) {
            this.onItemChanged();
            this.onFocusedDeviceChanged(this.$props.focusedDevice || []);
            this.onFollowedDevicesChanged();
        }
    }

    deactivated() {
        this.pollingActive = false;
        this.listRequests.invalidate();
        this.focusRequests.invalidate();
        this.stopUpdate();
    }

    beforeDestroy() {
        this.stopWatchingVisibility?.();
        this.pollingActive = false;
        this.listRequests.invalidate();
        this.focusRequests.invalidate();
        this.stopUpdate();
    }

    activated() {
        if (!this.pollingActive) {
            this.pollingActive = true;
            this.onItemChanged();
            this.onFocusedDeviceChanged(this.$props.focusedDevice || []);
            this.onFollowedDevicesChanged();
        }
    }

    stopUpdate() {
        if (this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    centerUpdate(value: any): void {
        this.map.center = value;
    }

    zoomUpdate(value: number): void {
        this.map.zoom = value;
    }

    deviceLocation(device: IDeviceCoordinatesDto): LatLng {
        return new LatLng(
            device.location.latitude,
            device.location.longitude
        );
    }

    handleMarkerClick(device: IDeviceCoordinatesDto) {
        this.$props.focusOnDevice(device.deviceId);
    }

    @Watch('devicesOnMapList', {immediate: true})
    onItemChanged() {
        this.listRequests.invalidate();
        if (!this.$props.devicesOnMapList.length) {
            this.deviceList = [];
            return;
        }

        if (!this.pollingActive || document.hidden) {
            return;
        }
        const request = this.listRequests.begin() as number;

        DeviceRepository
            .getCoordinatesByDeviceIdList([...this.$props.devicesOnMapList] as number[], this.listRequests.config(request))
            .then((data: IDeviceCoordinatesDto[]) => {
                if (this.pollingActive && !document.hidden && this.listRequests.isCurrent(request)) {
                    this.deviceList = data;
                    this.updateData();
                }
            })
            .catch(error => {
                if (this.pollingActive && !document.hidden && this.listRequests.isCurrent(request)) {
                    this.deviceList = [];
                    this.map.bounds = null;
                    this.$emit('load-error', error);
                }
            })
            .then(() => this.listRequests.finish(request));
    }

    @Watch('focusedDevice', {immediate: true, deep: true})
    onFocusedDeviceChanged(value: number[]) {
        this.focusRequests.invalidate();
        if (this.pollingActive && !document.hidden && value[0] > 0) {
            const request = this.focusRequests.begin() as number;
            const device = this.deviceList.find(q => q.deviceId === value[0]);
            if (Number.isFinite(device?.location?.latitude) && Number.isFinite(device?.location?.longitude) && device) {
                this.map.center = new LatLng(device.location.latitude, device.location.longitude);
            }

            DeviceRepository
                .getFullCoordinatesByDeviceId(value[0], this.focusRequests.config(request))
                .then(response => {
                    if (!this.pollingActive || document.hidden || !this.focusRequests.isCurrent(request)) {
                        return;
                    }
                    const latitude = parseFloat(String(response.coordinates?.latitude));
                    const longitude = parseFloat(String(response.coordinates?.longitude));
                    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
                        this.map.center = new LatLng(latitude, longitude);
                    }
                })
                .catch(error => {
                    if (this.pollingActive && !document.hidden && this.focusRequests.isCurrent(request)) {
                        this.$emit('load-error', error);
                    }
                })
                .then(() => this.focusRequests.finish(request));
        }
    }

    @Watch('followedDevices', {immediate: true})
    onFollowedDevicesChanged() {
        this.stopUpdate();
        if (!this.$props.followedDevices.length) {
            this.map.bounds = null;
            return;
        }

        if (!this.pollingActive || document.hidden) {
            return;
        }

        this.updateData();

        this.timer = setInterval(this.updateData, 5000);

    }

    updateData() {
        const devices = this.deviceList.filter(item => this.$props.followedDevices.includes(item.deviceId));
        if (!devices.length) {
            this.map.bounds = null;
            return;
        }

        if (devices.length === 1) {
            this.map.bounds = null;
            this.map.center = this.deviceLocation(devices[0]);
            return;
        }

        const firstLocation = this.deviceLocation(devices[0]);
        const bounds = new LatLngBounds(firstLocation, firstLocation);
        devices.slice(1).forEach(device => bounds.extend(this.deviceLocation(device)));
        this.map.bounds = bounds;
    }
}

export default DeviceMap;
</script>

<style lang="scss" scoped>

.device-map-component {
    height: 99%;

    .map-container {
        height: 100%;
        position: relative;

        &.collapsed {
            height: 50%;
        }
    }

    .device-status-container {
        height: 50%;
    }
}

</style>
